import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DataProvider, useUserEvents } from './data/DataProvider';
import { useInvalidateTrading } from './data/hooks';
import { useAuthGate } from './store/session';
import { OrderTicketProvider } from './ui/OrderTicket';
import { Shell, ThemeSync } from './ui/Shell';
import { ToastHost, toast } from './ui/toast';
import { Loading } from './ui/common';
import { ErrorBoundary } from './ui/ErrorBoundary';
import { rupee } from './lib/format';
import ExplorePage from './pages/ExplorePage';
import LoginPage from './pages/LoginPage';

// Split the screens a first-time visitor does not land on. The login and
// explore paths stay in the main chunk so the first paint needs one request.
//
// The landing page is the exception in the other direction: it is the front
// door for signed-out visitors, but it is dead weight for the signed-in
// trader who never sees it again, and it drags in its own stylesheet and
// hero animation. It gets its own chunk.
const LandingPage = lazy(() => import('./landing/LandingPage'));
const WatchlistPage = lazy(() => import('./pages/WatchlistPage'));
const InstrumentPage = lazy(() => import('./pages/InstrumentPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

/** Every navigation starts at the top; a router does not do this by itself. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

/**
 * Live order updates pushed on the authenticated socket. A fill that happened
 * because a resting order finally crossed has no request to hang a refresh
 * off, so this is the only thing that keeps the blotter honest.
 */
function LiveOrderEvents() {
  const invalidate = useInvalidateTrading();
  const qc = useQueryClient();

  useUserEvents((e) => {
    if (e.type !== 'order_update') return;
    const o = e.order;
    if (o.status === 'EXECUTED') {
      toast({
        kind: 'filled',
        title: `${o.side} ${o.qtyFilled} ${o.symbol} executed`,
        detail: o.avgFillPrice != null ? `at ${rupee(o.avgFillPrice)}` : undefined,
      });
    } else if (o.status === 'REJECTED') {
      toast({ kind: 'rejected', title: `Order rejected · ${o.symbol}` });
    } else if (o.status === 'CANCELLED') {
      toast({ kind: 'info', title: `Order cancelled · ${o.symbol}` });
    }
    invalidate();
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  });

  return null;
}

/** Routes that need a signed-in phone identity. */
function RequireSession({ children }: { children: React.ReactNode }) {
  const gate = useAuthGate();
  const location = useLocation();
  if (gate === 'unknown') return <Loading />;
  if (gate === 'signedOut') {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  const gate = useAuthGate();

  return (
    <ErrorBoundary>
      <ThemeSync />
      <DataProvider>
        <OrderTicketProvider>
          <ScrollToTop />
          <LiveOrderEvents />
          <Routes>
            {/* Login is the one route outside the shell — no nav, no tab bar. */}
            <Route
              path="/login"
              element={gate === 'authenticated' ? <Navigate to="/explore" replace /> : <LoginPage />}
            />

            {/*
              The root is the marketing page for a visitor who has not signed
              in, and the desk for one who has. It lives outside the Shell:
              the landing page brings its own header, footer and ground, and
              wrapping it in the app chrome would put a trading tab bar under
              a hero.
            */}
            <Route
              path="/"
              element={
                gate === 'unknown' ? (
                  <Loading />
                ) : gate === 'authenticated' ? (
                  <Navigate to="/explore" replace />
                ) : (
                  <Suspense fallback={<Loading />}>
                    <LandingPage />
                  </Suspense>
                )
              }
            />
            <Route
              path="*"
              element={
                <Shell>
                  <Suspense fallback={<Loading />}>
                    <Routes>
                      <Route path="/explore" element={<ExplorePage />} />
                      <Route path="/stocks/:symbol" element={<InstrumentPage />} />
                      <Route path="/watchlist" element={<WatchlistPage />} />
                      <Route
                        path="/portfolio"
                        element={
                          <RequireSession>
                            <PortfolioPage />
                          </RequireSession>
                        }
                      />
                      <Route
                        path="/orders"
                        element={
                          <RequireSession>
                            <OrdersPage />
                          </RequireSession>
                        }
                      />
                      <Route
                        path="/wallet"
                        element={
                          <RequireSession>
                            <WalletPage />
                          </RequireSession>
                        }
                      />
                      <Route
                        path="/reports"
                        element={
                          <RequireSession>
                            <ReportsPage />
                          </RequireSession>
                        }
                      />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      {/* Old bookmarks and typos land somewhere real. */}
                      <Route path="/positions" element={<Navigate to="/portfolio" replace />} />
                      <Route path="/holdings" element={<Navigate to="/portfolio" replace />} />
                      <Route path="*" element={<Navigate to="/explore" replace />} />
                    </Routes>
                  </Suspense>
                </Shell>
              }
            />
          </Routes>
          <ToastHost />
        </OrderTicketProvider>
      </DataProvider>
    </ErrorBoundary>
  );
}
